import { FormBuilder } from "@components/ui/FormBuilder";
import type { FieldConfig } from "@components/ui/FormBuilder";
import { createField } from "@components/ui/formFieldHelpers";
import {
  colorRenderer,
  selectFontRenderer,
  selectIntermedioRender,
} from "@components/ui/fieldRederers";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";
import type { CreateClienteFinalWithUserInput } from "@/lib/validaciones/createClienteFinalWithUsuario.validacion";
import { createClienteFinalWithUserSchema } from "@/lib/validaciones/createClienteFinalWithUsuario.validacion";
import { subirLogoClienteFinal } from "@/lib/logoClienteFinal";
import { useRegisterClienteFinalWithUsuario } from "@hooks/clientes-finales/useRegisterClienteFinalWithUsuario";

export function RegisterClienteFinalWithUsuarioForm({
  setOpen,
  queryKey,
}: {
  setOpen: (open: boolean) => void;
  queryKey: string[];
}) {
  const { user } = useCurrentUser();
  const isSuperAdmin = user?.rol.nombre === "SUPER_ADMIN";
  const { mutate } = useRegisterClienteFinalWithUsuario({
    queryKey: queryKey,
  });

  const defaultValues: CreateClienteFinalWithUserInput = {
    usuario: {
      nombreUsuario: "",
      correo: "",
      contrasena: "",
      nombre: "",
      apellidoPaterno: "",
      apellidoMaterno: "",
      idRol: 3,
    },
    cliente: {
      nombreEmpresa: "",
      idUsuarioAlta: isSuperAdmin ? "" : user?.id ? String(user.id) : "",
      logoMarca: undefined,
      logoMarcaInverso: undefined,
      colorPrimario: "",
      colorSecundario: "",
      colorTextoPrimario: "",
      tipografiaMarca: "",
    },
  };

  const fields: FieldConfig<CreateClienteFinalWithUserInput>[] = [
    createField<CreateClienteFinalWithUserInput>(
      "usuario.nombreUsuario",
      "text",
      {
        label: "Nombre de usuario",
        colSpan: "1",
        required: true,
        disabled: false,
        placeholder: " Nombre de usuario",
      },
    ),
    createField<CreateClienteFinalWithUserInput>("usuario.nombre", "text", {
      label: "Nombre",
      colSpan: "1",
      required: true,
      disabled: false,
      placeholder: " Nombre",
    }),
    createField<CreateClienteFinalWithUserInput>(
      "usuario.apellidoPaterno",
      "text",
      {
        label: "Apellido paterno",
        colSpan: "1",
        required: true,
        disabled: false,
        placeholder: " Apellido paterno",
      },
    ),
    createField<CreateClienteFinalWithUserInput>(
      "usuario.apellidoMaterno",
      "text",
      {
        label: "Apellido materno",
        colSpan: "1",
        required: true,
        disabled: false,
        placeholder: " Apellido paterno",
      },
    ),
    createField<CreateClienteFinalWithUserInput>("usuario.correo", "text", {
      label: "Correo",
      colSpan: "1",
      required: true,
      disabled: false,
      placeholder: " correo@ejemplo.com",
    }),
    createField<CreateClienteFinalWithUserInput, "password">(
      "usuario.contrasena",
      "password",
      {
        label: "Contraseña",
        colSpan: "1",
        required: true,
        disabled: false,
        placeholder: "*******",
      },
    ),
    createField<CreateClienteFinalWithUserInput>(
      "cliente.nombreEmpresa",
      "text",
      {
        label: "Nombre de la empresa",
        colSpan: "2",
        required: true,
        disabled: false,
        placeholder: " Nombre de la empresa",
      },
    ),
    // -- Customs
    createField<CreateClienteFinalWithUserInput, "fileImage">(
      "cliente.logoMarca",
      "fileImage",
      {
        label: "Logo (solo imagen png, jpg, jpeg, webp, svg)",
        description: "Se usa sobre fondos claros.",
        colSpan: "1",
        required: false,
      },
    ),
    createField<CreateClienteFinalWithUserInput, "fileImage">(
      "cliente.logoMarcaInverso",
      "fileImage",
      {
        label: "Logo inverso (opcional)",
        description:
          "Se usa en el modo obscuro del aprendiz. Si no lo subes, se usa el logo normal.",
        colSpan: "1",
        required: false,
        previewBackground: "dark",
        clearable: true,
      },
    ),
    createField<CreateClienteFinalWithUserInput, "color">(
      "cliente.colorPrimario",
      "color",
      {
        label: "Color Primario",
        colSpan: "1",
        required: true,
        disabled: false,
      },
    ),
    createField<CreateClienteFinalWithUserInput, "color">(
      "cliente.colorSecundario",
      "color",
      {
        label: "Color Secundario",
        colSpan: "1",
        required: true,
        disabled: false,
      },
    ),
    createField<CreateClienteFinalWithUserInput, "color">(
      "cliente.colorTextoPrimario",
      "color",
      {
        label: "Color de Texto Primario",
        colSpan: "1",
        required: true,
        disabled: false,
      },
    ),
    createField<CreateClienteFinalWithUserInput, "selectFont">(
      "cliente.tipografiaMarca",
      "selectFont",
      {
        label: "Tipografía",
        colSpan: "2",
        required: true,
        disabled: false,
      },
    ),
    // Solo si no es super admin
    ...(isSuperAdmin
      ? [
          createField<CreateClienteFinalWithUserInput, "selectIntermediario">(
            "cliente.idUsuarioAlta",
            "selectIntermediario",
            {
              label: "Intermediario del cliente final",
              colSpan: "2",
              required: true,
              disabled: false,
            },
          ),
        ]
      : []),
    // -- Customs
  ];

  const handleSubmit = async (
    data: CreateClienteFinalWithUserInput,
    helpers: { fileBag: Record<string, File | null> },
  ) => {
    const logoFile = helpers.fileBag["cliente.logoMarca"] ?? null;
    const logoInversoFile = helpers.fileBag["cliente.logoMarcaInverso"] ?? null;
    const [logoMarca, logoMarcaInverso] = await Promise.all([
      logoFile ? subirLogoClienteFinal(logoFile) : undefined,
      logoInversoFile ? subirLogoClienteFinal(logoInversoFile) : undefined,
    ]);

    mutate(
      {
        ...data,
        cliente: { ...data.cliente, logoMarca, logoMarcaInverso },
      },
      {
        onSuccess: () => {
          setOpen(false);
        },
      },
    );
  };

  return (
    <FormBuilder<CreateClienteFinalWithUserInput>
      schema={createClienteFinalWithUserSchema}
      defaultValues={defaultValues}
      fields={fields}
      onSubmit={handleSubmit}
      submitLabel="Registrar Cliente"
      registry={{
        color: colorRenderer<CreateClienteFinalWithUserInput>(),
        selectFont: selectFontRenderer<CreateClienteFinalWithUserInput>(),
        ...(isSuperAdmin
          ? {
              selectIntermediario:
                selectIntermedioRender<CreateClienteFinalWithUserInput>(),
            }
          : {}),
      }}
    />
  );
}

// import { useForm } from "react-hook-form";
// import { useState } from "react";
// import { zodResolver } from "@hookform/resolvers/zod";
// import { Input } from "@components/ui/input";
// import { Button } from "@components/ui/button";
// import {
//   Form,
//   FormControl,
//   FormField,
//   FormItem,
//   FormLabel,
//   FormMessage,
// } from "@components/ui/form";
// import { useRegisterClienteFinalWithUsuario } from "@hooks/clientes-finales/useRegisterClienteFinalWithUsuario";
// import type { CreateClienteFinalWithUserInput } from "@lib/validaciones/createClienteFinalWithUsuario.validacion";
// import { createClienteFinalWithUserSchema } from "@lib/validaciones/createClienteFinalWithUsuario.validacion";
// import { useCurrentUser } from "@hooks/useCurrentUser";
// import { ColorPicker } from "@components/ui/colorPicker";
// import { SelectTipografia } from "@components/ui/SelectTipografia";
// import { uploadPresignedFile } from "@retro-ia/shared/upload";

// export function RegisterClienteFinalWithUsuarioForm({
//   setOpen,
//   queryKey,
// }: {
//   setOpen: (open: boolean) => void;
//   queryKey: string[];
// }) {
//   const { user } = useCurrentUser();
//   const [logoFile, setLogoFile] = useState<File | null>(null);
//   const form = useForm<CreateClienteFinalWithUserInput>({
//     resolver: zodResolver(createClienteFinalWithUserSchema),
//     defaultValues: {
//       usuario: {
//         nombreUsuario: "",
//         correo: "",
//         contrasena: "",
//         nombre: "",
//         apellidoPaterno: "",
//         apellidoMaterno: "",
//         idRol: 3,
//       },
//       cliente: {
//         nombreEmpresa: "",
//         idUsuarioAlta: user?.id ?? 0,
//         logoMarca: undefined,
//         colorPrimarioMarca: "",
//         colorSecundarioMarca: "",
//         colorTerciarioMarca: "",
//         colorAcentoMarca: "",
//         tipografiaMarca: "",
//       },
//     },
//   });

//   const { mutate, isPending } = useRegisterClienteFinalWithUsuario({
//     queryKey: queryKey,
//   });

//   const onSubmit = async (data: CreateClienteFinalWithUserInput) => {
//     let logoMarca = undefined;
//     if (logoFile) {
//       const res = await uploadPresignedFile({
//         file: logoFile,
//         ruta: "public/img/clientesFinales/" + logoFile.name,
//         fileName: logoFile.name,
//         fileType: logoFile.type,
//         isPublic: true,
//       });
//       logoMarca = res;
//     }

//     mutate(
//       {
//         ...data,
//         cliente: { ...data.cliente, logoMarca },
//       },
//       {
//         onSuccess: () => {
//           form.reset();
//           setOpen(false);
//         },
//       }
//     );
//   };

//   return (
//     <>
//       <div className="flex justify-center items-center px-4">
//         <Form {...form}>
//           <form
//             onSubmit={form.handleSubmit(onSubmit)}
//             className="space-y-6 w-full"
//           >
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               {/* Campos del formulario */}
//               <FormField
//                 control={form.control}
//                 name="usuario.nombreUsuario"
//                 render={({ field }) => (
//                   <FormItem>
//                     <FormLabel>Nombre de usuario</FormLabel>
//                     <FormControl>
//                       <Input placeholder="Nombre de usuario" {...field} />
//                     </FormControl>
//                     <FormMessage />
//                   </FormItem>
//                 )}
//               />
//               <FormField
//                 control={form.control}
//                 name="usuario.nombre"
//                 render={({ field }) => (
//                   <FormItem>
//                     <FormLabel>Nombre</FormLabel>
//                     <FormControl>
//                       <Input placeholder="Nombre" {...field} />
//                     </FormControl>
//                     <FormMessage />
//                   </FormItem>
//                 )}
//               />
//               <FormField
//                 control={form.control}
//                 name="usuario.apellidoPaterno"
//                 render={({ field }) => (
//                   <FormItem>
//                     <FormLabel>Apellido paterno</FormLabel>
//                     <FormControl>
//                       <Input placeholder="Apellido" {...field} />
//                     </FormControl>
//                     <FormMessage />
//                   </FormItem>
//                 )}
//               />
//               <FormField
//                 control={form.control}
//                 name="usuario.apellidoMaterno"
//                 render={({ field }) => (
//                   <FormItem>
//                     <FormLabel>Apellido materno</FormLabel>
//                     <FormControl>
//                       <Input placeholder="Apellido Materno" {...field} />
//                     </FormControl>
//                     <FormMessage />
//                   </FormItem>
//                 )}
//               />
//               <FormField
//                 control={form.control}
//                 name="usuario.correo"
//                 render={({ field }) => (
//                   <FormItem>
//                     <FormLabel>Correo</FormLabel>
//                     <FormControl>
//                       <Input placeholder="correo@ejemplo.com" {...field} />
//                     </FormControl>
//                     <FormMessage />
//                   </FormItem>
//                 )}
//               />
//               <FormField
//                 control={form.control}
//                 name="usuario.contrasena"
//                 render={({ field }) => (
//                   <FormItem>
//                     <FormLabel>Contraseña</FormLabel>
//                     <FormControl>
//                       <Input type="text" placeholder="*******" {...field} />
//                     </FormControl>
//                     <FormMessage />
//                   </FormItem>
//                 )}
//               />
//               <FormField
//                 control={form.control}
//                 name="cliente.nombreEmpresa"
//                 render={({ field }) => (
//                   <FormItem className="md:col-span-2">
//                     <FormLabel>Nombre de la empresa</FormLabel>
//                     <FormControl>
//                       <Input placeholder="Nombre de la empresa" {...field} />
//                     </FormControl>
//                     <FormMessage />
//                   </FormItem>
//                 )}
//               />

//               {/* Input de archivo fuera del control del formulario */}
//               <div className="md:col-span-2">
//                 <label className="block text-sm font-medium mb-2">
//                   Logo (solo imagen png, jpg, jpeg, webp, svg)
//                 </label>
//                 <div className="flex flex-col gap-2">
//                   <label
//                     htmlFor="logo-upload"
//                     className="cursor-pointer px-4 py-2 rounded bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-200 transition flex items-center gap-2"
//                   >
//                     <svg
//                       className="w-5 h-5 text-gray-500"
//                       fill="none"
//                       stroke="currentColor"
//                       strokeWidth={2}
//                       viewBox="0 0 24 24"
//                     >
//                       <path
//                         strokeLinecap="round"
//                         strokeLinejoin="round"
//                         d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5m0 0l5 5m-5-5v12"
//                       />
//                     </svg>
//                     <span>
//                       {logoFile ? "Cambiar imagen" : "Selecciona una imagen"}
//                     </span>
//                   </label>
//                   <input
//                     id="logo-upload"
//                     type="file"
//                     accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
//                     className="hidden"
//                     onChange={(e) => {
//                       const file = e.target.files?.[0] ?? null;
//                       setLogoFile(file);
//                     }}
//                   />
//                 </div>
//               </div>

//               {[
//                 "colorPrimarioMarca",
//                 "colorSecundarioMarca",
//                 "colorTerciarioMarca",
//                 "colorAcentoMarca",
//               ].map((campo) => (
//                 <FormField
//                   key={campo}
//                   control={form.control}
//                   name={
//                     `cliente.${campo}` as
//                       | "cliente.colorPrimarioMarca"
//                       | "cliente.colorSecundarioMarca"
//                       | "cliente.colorTerciarioMarca"
//                       | "cliente.colorAcentoMarca"
//                   }
//                   render={({ field }) => (
//                     <FormItem>
//                       <FormLabel>
//                         {campo.replace("Marca", "").replace("color", "Color ")}
//                       </FormLabel>
//                       <FormControl>
//                         <ColorPicker {...field} value={field.value ?? ""} />
//                       </FormControl>
//                       <FormMessage />
//                     </FormItem>
//                   )}
//                 />
//               ))}

//               <FormField
//                 control={form.control}
//                 name="cliente.tipografiaMarca"
//                 render={({ field }) => (
//                   <FormItem className="md:col-span-2">
//                     <FormLabel>Tipografía</FormLabel>
//                     <FormControl>
//                       <SelectTipografia
//                         value={field.value ?? ""}
//                         onChange={(value) => field.onChange(value)}
//                       />
//                     </FormControl>
//                     <FormMessage />
//                   </FormItem>
//                 )}
//               />
//             </div>

//             <Button type="submit" disabled={isPending} className="w-full">
//               {isPending ? "Registrando..." : "Registrar Cliente Final"}
//             </Button>
//           </form>
//         </Form>
//       </div>
//       <div className="w-full rounded-md overflow-hidden shadow-lg mt-10">
//         <div
//           className="h-2 w-full"
//           style={{
//             background: `linear-gradient(to right, ${
//               form.watch("cliente.colorPrimarioMarca") || "#defaultColor"
//             }, ${
//               form.watch("cliente.colorSecundarioMarca") || "#defaultColor"
//             })`,
//           }}
//         />

//         <div className="flex items-center justify-between p-4 bg-[var(--background)]">
//           {/* Izquierda: logo preview */}
//           <div className="flex items-center justify-center w-24 h-16 bg-white rounded-md border mr-4">
//             {logoFile ? (
//               <img
//                 src={URL.createObjectURL(logoFile)}
//                 alt="Logo preview"
//                 className="w-full h-full object-contain rounded-md"
//               />
//             ) : (
//               <span className="text-xs text-gray-400">Sin logo</span>
//             )}
//           </div>

//           {/* Centro: texto con tipografía seleccionada */}
//           <div
//             className="text-center flex-1 px-4"
//             style={{
//               fontFamily: `var(--font-${
//                 form.watch("cliente.tipografiaMarca") || "sans"
//               })`,
//             }}
//           >
//             <h2 className="text-2xl font-bold">
//               Vista previa de la tipografía
//             </h2>
//             <p className="mt-2">
//               Esta es una vista previa de cómo se verá la tipografía
//               seleccionada en el cliente final.
//             </p>
//           </div>

//           {/* Derecha: botón color terciario */}
//           <Button
//             size={"lg"}
//             variant="destructive"
//             className="text-black"
//             style={{
//               backgroundColor:
//                 (form.watch("cliente.colorTerciarioMarca") ||
//                   "#defaultColor") ??
//                 undefined,
//               color: form.watch("cliente.colorAcentoMarca") || "#000",
//             }}
//           >
//             boton
//           </Button>
//         </div>
//       </div>
//     </>
//   );
// }
